import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, ChevronDown, ChevronRight, Ban, ShieldCheck, Phone, Mail, AlertTriangle, Gift, Star, ThumbsUp, StickyNote, ShieldAlert } from "lucide-react";
import CustomerDetail from "@/components/admin/CustomerDetail";
import ClubPointsButton from "@/components/admin/ClubPointsButton";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

export default function CustomersManager({ token, onAuthError }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openEmail, setOpenEmail] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [showAttempts, setShowAttempts] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/customers`, auth).then((r) => setCustomers(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
    axios.get(`${API}/dnr/attempts`, auth).then((r) => setAttempts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const block = async (c) => {
    const reason = window.prompt(`Add ${c.name || c.email} to the Do-Not-Rent list? Enter a reason (optional):`, "");
    if (reason === null) return;
    try {
      const { data } = await axios.post(`${API}/dnr`, { email: c.email, phone: c.phone, name: c.name, reason }, auth);
      toast.success(data.auto_declined > 0
        ? `Added to Do-Not-Rent list. ${data.auto_declined} pending reservation request(s) auto-declined.`
        : "Added to Do-Not-Rent list.");
      load();
    } catch (err) { if (err.response?.status === 401) onAuthError?.(); else toast.error("Could not update."); }
  };

  const unblock = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email} from the Do-Not-Rent list?`)) return;
    try {
      await axios.post(`${API}/dnr/remove`, { email: c.email }, auth);
      toast.success("Removed from Do-Not-Rent list.");
      load();
    } catch (err) { if (err.response?.status === 401) onAuthError?.(); else toast.error("Could not update."); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  const filtered = customers.filter((c) => {
    const s = q.toLowerCase();
    return !s || (c.name || "").toLowerCase().includes(s) || c.email.includes(s) || (c.phone || "").includes(s);
  });
  const dnrCount = customers.filter((c) => c.dnr).length;

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-white/50 text-sm">{customers.length} customer(s){dnrCount > 0 && <span className="text-red-400"> · {dnrCount} on Do-Not-Rent</span>}. Profiles are built automatically from bookings.</p>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone"
            className="bg-[#0C0C0C] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-[#F2B705] w-64" />
        </div>
      </div>

      {attempts.length > 0 && (
        <div data-testid="blocked-attempts-panel" className="mb-4 bg-[#0C0C0C] border border-red-500/30 rounded-lg overflow-hidden">
          <button data-testid="blocked-attempts-toggle" onClick={() => setShowAttempts(!showAttempts)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left">
            <ShieldAlert size={16} className="text-red-400" />
            <span className="text-white font-semibold text-sm">Blocked Attempts (Do-Not-Rent)</span>
            <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-bold">{attempts.length}</span>
            <span className="text-white/40 text-xs mr-auto">— banned customers who tried to sneak back in</span>
            {showAttempts ? <ChevronDown size={15} className="text-white/40" /> : <ChevronRight size={15} className="text-white/40" />}
          </button>
          {showAttempts && (
            <div className="border-t border-red-500/20 px-4 py-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Name Used</th>
                    <th className="py-2 pr-3">Email / Phone Used</th>
                    <th className="py-2 pr-3">Vehicle & Dates</th>
                    <th className="py-2">Matched Block</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.slice(0, 30).map((a) => (
                    <tr key={a.id} data-testid={`blocked-attempt-${a.id}`} className="border-b border-white/5">
                      <td className="py-2 pr-3 text-white/60 text-xs whitespace-nowrap">{fmtDateTime(a.at)}</td>
                      <td className="py-2 pr-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${a.kind === "reservation_request" ? "bg-orange-500/15 text-orange-300 border-orange-400/40" : "bg-red-500/15 text-red-300 border-red-400/40"}`}>{a.kind === "reservation_request" ? "Reservation" : "Booking"}</span></td>
                      <td className="py-2 pr-3 text-white font-semibold">{a.name}</td>
                      <td className="py-2 pr-3 text-white/70 text-xs">{a.email}<br />{a.phone}</td>
                      <td className="py-2 pr-3 text-white/70 text-xs">{a.vehicle}<br />{a.pickup_date} → {a.return_date}</td>
                      <td className="py-2 text-red-400 text-xs">{a.matched_dnr_email}{a.reason ? ` (${a.reason})` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 ? <p className="text-white/40">No customers found.</p> : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <div key={c.email} data-testid={`customer-row-${c.email}`} className={`bg-[#0C0C0C] border rounded-lg overflow-hidden ${c.dnr ? "border-red-500/40" : "border-white/10"}`}>
              <button onClick={() => setOpenEmail(openEmail === c.email ? null : c.email)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  {openEmail === c.email ? <ChevronDown size={16} className="text-white/40 shrink-0" /> : <ChevronRight size={16} className="text-white/40 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2 flex-wrap">
                      {c.name}
                      {c.credits_available > 0 && <span data-testid={`owed-badge-${c.email}`} className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F2B705] text-black font-bold flex items-center gap-1 shadow-[0_0_10px_rgba(242,183,5,0.5)]"><Gift size={10} /> Owed{c.credits_summary ? `: ${c.credits_summary}` : ""}</span>}
                      {c.great_customer && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><ThumbsUp size={9} /> VIP</span>}
                      {c.problem_customer && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 flex items-center gap-1"><AlertTriangle size={9} /> Flagged</span>}
                      {c.rating > 0 && <span className="text-[10px] text-[#F2B705] flex items-center gap-0.5"><Star size={10} className="fill-[#F2B705]" /> {c.rating}</span>}
                      {c.notes_count > 0 && <span className="text-[10px] text-white/40 flex items-center gap-0.5"><StickyNote size={10} /> {c.notes_count}</span>}
                      {c.dnr && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/40 flex items-center gap-1"><Ban size={10} /> Do Not Rent</span>}
                    </div>
                    <div className="text-white/40 text-xs truncate">{c.email}{c.phone && ` · ${c.phone}`}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm shrink-0">
                  <span className="text-white/50 hidden sm:inline">{c.bookings_count} booking{c.bookings_count !== 1 ? "s" : ""}</span>
                  <span className="text-green-400">{money(c.total_paid)}</span>
                  {c.balance_due > 0 && <span className="text-red-400 hidden md:inline">{money(c.balance_due)} due</span>}
                </div>
              </button>
              {openEmail === c.email && (
                <div className="border-t border-white/10 p-4 bg-[#0A0A0A]" data-testid={`customer-detail-${c.email}`}>
                  {c.dnr && c.dnr_reason && <div className="mb-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 flex items-center gap-2"><AlertTriangle size={14} /> {c.dnr_reason}</div>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                    <Stat label="Bookings" value={c.bookings_count} />
                    <Stat label="Total Paid" value={money(c.total_paid)} tone="ok" />
                    <Stat label="Balance Due" value={money(c.balance_due)} tone={c.balance_due > 0 ? "danger" : "default"} />
                    <Stat label="Last Booking" value={fmtDate(c.last_booking)} />
                  </div>
                  <div className="flex flex-wrap gap-3 mb-4 text-sm">
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-[#F2B705] hover:underline"><Phone size={13} /> Call</a>
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-white/70 hover:text-[#F2B705]"><Mail size={13} /> Email</a>
                  </div>

                  <h4 className="text-sm font-semibold text-white/70 mb-2">Rental History</h4>
                  <div className="space-y-1.5">
                    {c.history.map((h) => (
                      <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm">
                        <span><span className="text-[#F2B705] font-medium">{h.vehicle}</span> · {h.pickup_date} → {h.return_date}</span>
                        <span className="text-white/50">{money(h.amount_paid)} paid {h.status === "approved" && <span className="text-green-400">· approved</span>}</span>
                      </div>
                    ))}
                  </div>

                  <CustomerDetail token={token} customer={c} onChanged={load} />

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <ClubPointsButton customer={c} token={token} onSaved={load} />
                    {c.dnr ? (
                      <button data-testid={`customer-unblock-${c.email}`} onClick={() => unblock(c)} className="flex items-center gap-1.5 border border-green-500/30 text-green-400 text-sm px-4 py-2 rounded-md hover:bg-green-500 hover:text-white transition-colors"><ShieldCheck size={15} /> Remove from Do-Not-Rent</button>
                    ) : (
                      <button data-testid={`customer-block-${c.email}`} onClick={() => block(c)} className="flex items-center gap-1.5 border border-red-500/30 text-red-400 text-sm px-4 py-2 rounded-md hover:bg-red-500 hover:text-white transition-colors"><Ban size={15} /> Add to Do-Not-Rent</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const tones = { default: "text-white", ok: "text-green-400", danger: "text-red-400" };
  return <div><div className="text-white/40 text-xs">{label}</div><div className={`font-bold ${tones[tone]}`}>{value}</div></div>;
}
